import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { cn } from "../../../lib";

const checkboxVariants = cva("checkbox__input", {
  variants: {
    color: {
      default:
        "peer cursor-pointer rounded border-2 border-slate-400 transition-colors duration-300 ease-in-out " +
        "checked:bg-blue-700 checked:hover:bg-blue-700 checked:disabled:border-slate-300 checked:disabled:bg-slate-300 checked:disabled:hover:bg-slate-300 " +
        "disabled:hover:none disabled:cursor-not-allowed disabled:border-slate-200 disabled:indeterminate:border-slate-300 disabled:indeterminate:bg-slate-300 disabled:hover:bg-transparent " +
        "hover:border-blue-700 hover:bg-blue-50 focus:ring-transparent " +
        "indeterminate:bg-blue-700 indeterminate:disabled:hover:bg-slate-300",
    },
    size: {
      large: "size-6",
      medium: "size-4",
      small: "size-[4px]",
    },
  },
  defaultVariants: {
    color: "default",
    size: "medium",
  },
});

export interface CheckboxVariants {
  color?: "default";
  size?: "small" | "medium" | "large";
}

interface CheckboxProps
  extends VariantProps<typeof checkboxVariants>,
    Omit<
      React.InputHTMLAttributes<HTMLInputElement>,
      "size" | "color" | "style"
    > {
  className?: string;
  label?: string;
  size?: "medium" | "small" | "large" | undefined;
  color?: "default" | undefined;
  indeterminate?: boolean;
  style?: {
    visibility: "hidden" | "visible";
  };
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (props: CheckboxProps, ref: React.Ref<HTMLInputElement>) => {
    const {
      color = "default",
      size = "medium",
      className,
      label,
      indeterminate,
      ...rest
    } = props;
    const innerRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => innerRef.current!, [innerRef]);

    useEffect(() => {
      if (typeof indeterminate === "boolean" && innerRef?.current) {
        innerRef.current.indeterminate = indeterminate;
      }
    }, [innerRef, indeterminate]);

    return (
      <div className="checkbox">
        <input
          type="checkbox"
          className={cn(checkboxVariants({ color, size }), className)}
          {...rest}
          ref={innerRef}
        />
        {label && <label className="ml-2">{label}</label>}
      </div>
    );
  },
);

Checkbox.displayName = "Checkbox";

export default Checkbox;
export type { CheckboxProps };
